import { type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padded?: boolean;
  children: ReactNode;
}

export function Card({ padded, className, children, ...rest }: CardProps) {
  return (
    <div className={cn('ui-card', padded && 'ui-card--padded', className)} {...rest}>
      {children}
    </div>
  );
}

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}

export function CardHeader({
  title,
  description,
  actions,
  className,
  children,
  ...rest
}: CardHeaderProps) {
  // If children are provided, render them as-is (escape hatch for bespoke header markup).
  // Otherwise use the structured title/description/actions layout.
  if (children !== undefined) {
    return (
      <div className={cn('ui-card__header', className)} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <div className={cn('ui-card__header', className)} {...rest}>
      <div>
        {title !== undefined ? <h3 className="ui-card__title">{title}</h3> : null}
        {description ? <p className="ui-card__description">{description}</p> : null}
      </div>
      {actions ? <div>{actions}</div> : null}
    </div>
  );
}

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CardBody({ className, children, ...rest }: CardBodyProps) {
  return (
    <div className={cn('ui-card__body', className)} {...rest}>
      {children}
    </div>
  );
}

export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CardFooter({ className, children, ...rest }: CardFooterProps) {
  return (
    <div className={cn('ui-card__footer', className)} {...rest}>
      {children}
    </div>
  );
}
